/**
 * CapacityRiskCard - Card for displaying team member capacity risks
 * Used in Gaps & Risks page Capacity Risks tab
 */
import React from 'react';
import { Users, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';

export default function CapacityRiskCard({ risk }) {
  const riskLevelColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  return (
    <div 
      className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5"
      data-testid={`capacity-risk-${risk.userId || risk.user_id}`}
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
          <Users className="h-6 w-6 text-blue-400" />
        </div>

        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {risk.userName || risk.user_name}
              </h3>
              <p className="text-sm text-[#6B6B6B]">{risk.userRole || risk.user_role}</p>
            </div>
            <span className={`px-3 py-1 text-xs font-bold rounded ${riskLevelColors[risk.riskLevel || risk.risk_level] || riskLevelColors.medium}`}>
              {(risk.riskLevel || risk.risk_level || 'medium').toUpperCase()} RISK
            </span>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
              <p className="text-xs text-[#6B6B6B] mb-1">Active</p>
              <p className="text-2xl font-bold text-white">
                {risk.activeObligations || risk.active_count || 0}
              </p>
            </div>
            <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
              <p className="text-xs text-[#6B6B6B] mb-1">Overdue</p>
              <p className="text-2xl font-bold text-red-400">
                {risk.overdueCount || risk.overdue_count || 0}
              </p>
            </div>
            <div className="p-3 bg-[#0A0A0A] rounded-lg text-center">
              <p className="text-xs text-[#6B6B6B] mb-1">Avg Progress</p>
              <p className="text-2xl font-bold text-white">
                {Math.round(risk.avgProgress || risk.avg_progress || 0)}%
              </p>
            </div>
          </div>

          {/* Workload bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-[#6B6B6B] mb-1">
              <span>Workload Capacity</span>
              <span>{risk.workload_percentage || 85}%</span>
            </div>
            <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${
                  (risk.workload_percentage || 85) > 90 ? 'bg-red-500' :
                  (risk.workload_percentage || 85) > 70 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${risk.workload_percentage || 85}%` }}
              />
            </div>
          </div>

          {/* Recommendation */}
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-sm text-blue-300">
              <strong className="text-blue-400">Recommendation:</strong>{' '}
              {risk.recommendation || 'Consider redistributing workload or extending deadlines.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
